import {
  Button,
  List,
  ListItem,
  makeStyles,
  SearchBox,
  SelectionItemId,
} from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import {
  SearchFilter,
  SearchFilterValue,
} from "../../models/SearchConfiguration";
import { SkanskaColorTokens } from "../../style/colors";
import {
  fromFilterListItemValue,
  getFilterValuesForSelection,
  getUniqueFilterValues,
  normalizeFilterValue,
  toFilterListItemValue,
} from "./filterValueUtils";

interface SearchFilterCardProps {
  filter: SearchFilter;
  selectedFilterValues: SearchFilterValue[];
  onFilterValuesChange: (selectedValues: SearchFilterValue[]) => void;
}

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    minWidth: "500px",
    maxWidth: "500px",
  },
  list: {
    backgroundColor: SkanskaColorTokens.color_secondary_dim_white,
    padding: "15px",
    marginTop: "5px",
    maxHeight: "300px",
    overflow: "auto",
  },
  searchBox: {
    marginTop: "10px",
  },
  listItem: {
    lineHeight: "20px",
  },
  listItemCheckmark: {
    alignSelf: "flex-start",
  },
  listItemLabel: {
    alignSelf: "flex-start",
    lineHeight: "normal",
    paddingTop: "2px",
  },
  clearFilterButton: {
    marginTop: "5px",
    alignSelf: "flex-start",
  },
});

export const SearchFilterCard = (props: SearchFilterCardProps) => {
  const { filter, onFilterValuesChange, selectedFilterValues } = props;
  const classes = useStyles();
  const { t } = useTranslation();

  const filterValues = getUniqueFilterValues(
    filter.fieldUniqueName,
    filter.values
  );
  const [options, setOptions] = useState<SearchFilterValue[]>(filterValues);

  const getFilterValueLabel = (value: SearchFilterValue) =>
    value.displayValue || t("emptyFilterValue");

  const handleSearch = (newValue: string) => {
    const newOptions = filterValues.filter(
      (val) =>
        getFilterValueLabel(val)
          .toLowerCase()
          .indexOf(newValue.toLowerCase()) !== -1
    );
    setOptions(newOptions);
  };

  const updateFilterValues = (newFilterValues: string[]) => {
    const selectedFilterValues = getFilterValuesForSelection(
      filter.fieldUniqueName,
      filter.values,
      newFilterValues
    );

    onFilterValuesChange(selectedFilterValues);
  };

  const handleSelectionChange = (items: SelectionItemId[]) => {
    const selectedValues = items.map((item) =>
      fromFilterListItemValue(item.toString())
    );
    const selectedFilterValues = getFilterValuesForSelection(
      filter.fieldUniqueName,
      filter.values,
      selectedValues
    );

    onFilterValuesChange(selectedFilterValues);
  };

  const handleClearFilter = () => updateFilterValues([]);

  return (
    <div className={classes.container}>
      <SearchBox
        className={classes.searchBox}
        placeholder={`${t("searchFilter")}${filter.fieldDisplayName.toLowerCase()}`}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            handleSearch(e.currentTarget.value);
          }
        }}
        onSubmit={(e) => {
          e.preventDefault();
          handleSearch(e.currentTarget.value);
        }}
      />
      <Button
        appearance="subtle"
        size="small"
        className={classes.clearFilterButton}
        onClick={handleClearFilter}
      >
        {t("clearFilter")}
      </Button>
      <List
        tabIndex={0}
        className={classes.list}
        selectionMode="multiselect"
        aria-label={`${t("filterListAriaLabel")}${filter.fieldDisplayName}`}
        selectedItems={Array.from(
          new Set(
            selectedFilterValues.map((selectedValue) =>
              toFilterListItemValue(
                normalizeFilterValue(
                  filter.fieldUniqueName,
                  selectedValue.value
                )
              )
            )
          )
        )}
        onSelectionChange={(_ev, data) =>
          handleSelectionChange(data.selectedItems)
        }
      >
        {options.map((value) => (
          <ListItem
            className={`filter-item-top ${classes.listItem}`}
            key={toFilterListItemValue(value.value)}
            value={toFilterListItemValue(value.value)}
            aria-label={getFilterValueLabel(value)}
            checkmark={{
              "aria-label": getFilterValueLabel(value),
              className: classes.listItemCheckmark,
            }}
          >
            <span className={classes.listItemLabel}>
              {getFilterValueLabel(value)}
            </span>
          </ListItem>
        ))}
      </List>
    </div>
  );
};
